<template>
  <!-- The list is drawn twice and slides by one copy, so the loop never jumps. -->
  <div class="logo-marquee">
    <ul
      ref="track"
      class="logo-track list-unstyled mb-0"
      :class="{ 'is-small-print': smallPrint }"
    >
      <li
        v-for="(logo, index) in [...logos, ...logos]"
        :key="`${logo.name}-${index}`"
        :aria-hidden="index >= logos.length"
      >
        <!-- The second copy is decoration: hidden from screen readers and skipped by the keyboard. -->
        <a
          :href="logo.url"
          target="_blank"
          rel="noopener noreferrer"
          class="logo-link"
          :tabindex="index >= logos.length ? -1 : undefined"
        >
          <img
            :src="logo.src"
            :alt="index < logos.length ? logo.name : ''"
            :title="logo.name"
            class="proof-logo"
            :class="{ 'is-stacked': logo.stacked }"
            loading="lazy"
          />
        </a>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { useElementSize } from "@vueuse/core";
import type { MarqueeLogo } from "@/components/proof/solderaLogos";
import { computed, useTemplateRef } from "vue";

// Every row moves at this speed whatever its length, so rows side by side drift together.
const PIXELS_PER_SECOND = 12;
const FALLBACK_DURATION = "60s";

withDefaults(
  defineProps<{
    logos: MarqueeLogo[];
    /** The logos carry names in small type, so they are drawn taller to stay legible. */
    smallPrint?: boolean;
  }>(),
  { smallPrint: false }
);

const track = useTemplateRef<HTMLUListElement>("track");
const { width } = useElementSize(track, undefined, { box: "border-box" });

// One loop slides the track by half its width: one copy of the list.
const scrollDuration = computed(() =>
  width.value ? `${width.value / 2 / PIXELS_PER_SECOND}s` : FALLBACK_DURATION
);
</script>

<style scoped lang="scss">
.logo-marquee {
  overflow: hidden;
  // Logos fade in and out at the edges instead of being cut off.
  mask-image: linear-gradient(
    to right,
    transparent,
    #000 8%,
    #000 92%,
    transparent
  );
}

.logo-track {
  display: flex;
  align-items: center;
  gap: 2.5rem;
  // The trailing gap makes both copies the same width, so sliding by half lands exactly on the second copy.
  padding-right: 2.5rem;
  // Room for the focus ring, which the marquee would otherwise clip.
  padding-block: 4px;
  width: max-content;
  animation: logo-scroll v-bind(scrollDuration) linear infinite;

  // Holding still under the pointer or keyboard focus, so a logo can be clicked. Not on any focus: a clicked
  // link keeps it after its tab opens, which would freeze the row until the user clicked elsewhere.
  .logo-marquee:hover &,
  .logo-marquee:has(:focus-visible) & {
    animation-play-state: paused;
  }
}

@keyframes logo-scroll {
  to {
    transform: translateX(-50%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .logo-marquee {
    overflow-x: auto;
  }

  .logo-track {
    animation: none;
  }
}

.logo-link {
  display: block;
  border-radius: 4px;

  &:hover .proof-logo,
  &:focus-visible .proof-logo {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }
}

// One white silhouette per logo, so colour logos made for light pages read on the dark panel.
.proof-logo {
  display: block;
  height: 22px;
  max-width: 110px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  opacity: 0.9;

  &.is-stacked {
    height: 32px;
  }
}

.is-small-print .proof-logo {
  height: 32px;
  max-width: 120px;
}
</style>
